
import { Link } from 'react-router-dom';

export default function HowItWorks() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center h-24">
            <Link to="/" className="flex items-center cursor-pointer">
              <img
                src="https://static.readdy.ai/image/d58764125c7980aecc839df102219a66/870d5e2d6ad7495c633feeec16bc35ed.png"
                alt="Care Entry"
                className="h-20 w-auto"
              />
            </Link>
            <div className="flex items-center space-x-8">
              <Link to="/" className="text-gray-600 hover:text-gray-800 cursor-pointer font-medium">
                Home
              </Link>
              <Link to="/how-it-works" className="text-purple-600 font-semibold cursor-pointer">
                How It Works
              </Link>
              <Link to="/#features" className="text-gray-600 hover:text-gray-800 cursor-pointer font-medium">
                Features
              </Link>
              <Link to="/#testimonials" className="text-gray-600 hover:text-gray-800 cursor-pointer font-medium">
                Testimonials
              </Link>
              <Link to="/#pricing" className="text-gray-600 hover:text-gray-800 cursor-pointer font-medium">
                Pricing
              </Link>
              <Link to="/#pricing" className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-6 py-2 rounded-full hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer font-medium">
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-5xl lg:text-6xl font-light text-gray-800 mb-6" style={{fontFamily: '"Playfair Display", serif'}}>
            From Notes to Reports in 3 Simple Steps
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed font-light">
            See exactly how Care Entry transforms your daily care documentation into professional reports that impress Regional Center and IHSS coordinators.
          </p>
          <div className="bg-white rounded-2xl shadow-xl p-8 inline-block">
            <div className="flex items-center justify-center space-x-6 text-sm text-gray-600">
              <div className="flex items-center">
                <i className="ri-time-line text-purple-600 mr-2"></i>
                <span className="font-light">Takes 2 minutes per session</span>
              </div>
              <div className="flex items-center">
                <i className="ri-smartphone-line text-purple-600 mr-2"></i>
                <span className="font-light">Works on any device</span>
              </div>
              <div className="flex items-center">
                <i className="ri-team-line text-purple-600 mr-2"></i>
                <span className="font-light">Built for care teams</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Step 1 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-full mr-6 text-2xl font-medium">
                  1
                </div>
                <h2 className="text-4xl font-light text-gray-800" style={{fontFamily: '"Playfair Display", serif'}}>Caregiver Logs a Session</h2>
              </div>

              <p className="text-xl text-gray-600 mb-8 leading-relaxed font-light">
                Caregivers (AST, ILS coach, IHSS provider, respite staff, etc.) quickly fill in a digital note with time, activity, and progress.
              </p>

              <div className="space-y-6">
                <div className="bg-purple-50 rounded-xl p-6">
                  <h3 className="text-lg font-medium text-gray-800 mb-3" style={{fontFamily: '"Playfair Display", serif'}}>Quick Session Notes Include:</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center text-gray-700">
                      <i className="ri-check-line text-purple-600 mr-3"></i>
                      Time in/out with automatic tracking
                    </li>
                    <li className="flex items-center text-gray-700">
                      <i className="ri-check-line text-purple-600 mr-3"></i>
                      Activities completed & goals worked on
                    </li>
                    <li className="flex items-center text-gray-700">
                      <i className="ri-check-line text-purple-600 mr-3"></i>
                      Mood, behaviors & medication reminders
                    </li>
                    <li className="flex items-center text-gray-700">
                      <i className="ri-check-line text-purple-600 mr-3"></i>
                      Any concerns or celebrations to share
                    </li>
                  </ul>
                </div>

                <div className="bg-yellow-50 rounded-xl p-6">
                  <h3 className="text-lg font-medium text-gray-800 mb-3" style={{fontFamily: '"Playfair Display", serif'}}>Optional Safety Features:</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center text-gray-700">
                      <i className="ri-camera-line text-yellow-600 mr-3"></i>
                      Attach photos of activities or progress
                    </li>
                    <li className="flex items-center text-gray-700">
                      <i className="ri-alert-line text-yellow-600 mr-3"></i>
                      Flag incidents like injuries or dangerous behaviors
                    </li>
                    <li className="flex items-center text-gray-700">
                      <i className="ri-notification-line text-yellow-600 mr-3"></i>
                      Automatic parent notifications for urgent items
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <img
                src="https://public.readdy.ai/ai/img_res/24a545df23b4a67e53dce271fbe1ead5.jpg"
                alt="Caregiver logging session notes"
                className="rounded-2xl shadow-2xl w-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Step 2 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="lg:order-2">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-full mr-6 text-2xl font-medium">
                  2
                </div>
                <h2 className="text-4xl font-light text-gray-800" style={{fontFamily: '"Playfair Display", serif'}}>Parents Stay in the Loop</h2>
              </div>

              <p className="text-xl text-gray-600 mb-8 leading-relaxed font-light">
                Parents can view all notes in real time. Action plans and goals are kept in one place so everyone is consistent.
              </p>

              <div className="space-y-6">
                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="text-lg font-medium text-gray-800 mb-3" style={{fontFamily: '"Playfair Display", serif'}}>Real-Time Updates:</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center text-gray-700">
                      <i className="ri-notification-line text-green-600 mr-3"></i>
                      Get notified when sessions are completed
                    </li>
                    <li className="flex items-center text-gray-700">
                      <i className="ri-eye-line text-green-600 mr-3"></i>
                      See what happened during each visit
                    </li>
                    <li className="flex items-center text-gray-700">
                      <i className="ri-message-line text-green-600 mr-3"></i>
                      Ask questions or give feedback instantly
                    </li>
                    <li className="flex items-center text-gray-700">
                      <i className="ri-calendar-line text-green-600 mr-3"></i>
                      Track patterns and progress over time
                    </li>
                  </ul>
                </div>

                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="text-lg font-medium text-gray-800 mb-3" style={{fontFamily: '"Playfair Display", serif'}}>Consistent Care Plans:</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center text-gray-700">
                      <i className="ri-target-line text-blue-600 mr-3"></i>
                      IEP & IPP goals accessible to all caregivers
                    </li>
                    <li className="flex items-center text-gray-700">
                      <i className="ri-list-check text-blue-600 mr-3"></i>
                      Action plans everyone can follow
                    </li>
                    <li className="flex items-center text-gray-700">
                      <i className="ri-medicine-bottle-line text-blue-600 mr-3"></i>
                      Medication schedules & reminders
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="lg:order-1">
              <img
                src="https://readdy.ai/api/search-image?query=SDP%20parent%20care%20manager%20reviewing%20real-time%20care%20updates%20on%20tablet%20at%20kitchen%20table%20with%20peaceful%20expression%2C%20natural%20home%20lighting%2C%20authentic%20family%20moment%20showing%20relief%20and%20connection%2C%20modern%20home%20setting%20with%20organized%20care%20coordination%2C%20sense%20of%20being%20informed%20and%20in%20control&width=600&height=500&seq=step2-parent-updates&orientation=landscape"
                alt="Parent reviewing care updates"
                className="rounded-2xl shadow-2xl w-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Step 3 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-full mr-6 text-2xl font-medium">
                  3
                </div>
                <h2 className="text-4xl font-light text-gray-800" style={{fontFamily: '"Playfair Display", serif'}}>Generate Reports Instantly</h2>
              </div>

              <p className="text-xl text-gray-600 mb-8 leading-relaxed font-light">
                With one click, create professional summaries for Regional Center or IHSS. No more scrambling before IPP, ISP, or yearly reassessments.
              </p>

              <div className="space-y-6">
                <div className="bg-purple-50 rounded-xl p-6">
                  <h3 className="text-lg font-medium text-gray-800 mb-3" style={{fontFamily: '"Playfair Display", serif'}}>Professional Reports Include:</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center text-gray-700">
                      <i className="ri-file-text-line text-purple-600 mr-3"></i>
                      Hours worked & activities completed
                    </li>
                    <li className="flex items-center text-gray-700">
                      <i className="ri-bar-chart-line text-purple-600 mr-3"></i>
                      Progress toward IEP & IPP goals
                    </li>
                    <li className="flex items-center text-gray-700">
                      <i className="ri-calendar-check-line text-purple-600 mr-3"></i>
                      Attendance patterns & consistency
                    </li>
                    <li className="flex items-center text-gray-700">
                      <i className="ri-lightbulb-line text-purple-600 mr-3"></i>
                      Recommended adjustments to care plan
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-800 text-white rounded-xl p-6">
                  <h3 className="text-lg font-medium mb-3" style={{fontFamily: '"Playfair Display", serif'}}>✨ Before vs. After</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-300 mb-2 font-light">❌ Old Way:</p>
                      <p className="font-light">3-4 hours scrambling through papers before each meeting</p>
                    </div>
                    <div>
                      <p className="text-gray-300 mb-2 font-light">✅ With Care Entry:</p>
                      <p className="font-light">Professional report ready in 30 seconds</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <h4 className="text-lg font-medium text-gray-800 mb-3" style={{fontFamily: '"Playfair Display", serif'}}>Perfect for:</h4>
                <div className="flex flex-wrap gap-3">
                  <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">Regional Center IPP</span>
                  <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">IHSS Reassessments</span>
                  <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">School IEP Meetings</span>
                  <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">Medical Appointments</span>
                </div>
              </div>
            </div>

            <div>
              <img
                src="https://readdy.ai/api/search-image?query=parent%20care%20manager%20generating%20professional%20care%20report%20on%20laptop%20with%20satisfied%20expression%2C%20clean%20organized%20home%20office%20setting%2C%20natural%20lighting%2C%20authentic%20moment%20showing%20efficiency%20and%20confidence%2C%20modern%20technology%20for%20care%20coordination%2C%20sense%20of%20accomplishment%20and%20preparedness&width=600&height=500&seq=step3-report-generation&orientation=landscape"
                alt="Generating professional care reports"
                className="rounded-2xl shadow-2xl w-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Summary */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-800 mb-4" style={{fontFamily: '"Playfair Display", serif'}}>The Result? Peace of Mind.</h2>
            <p className="text-xl text-gray-600 font-light">See how these 3 simple steps transform your family's care coordination</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
                <i className="ri-time-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-medium text-gray-800 mb-4" style={{fontFamily: '"Playfair Display", serif'}}>Save 4+ Hours Per Month</h3>
              <p className="text-gray-600 font-light">
                No more scrambling to collect notes or recreating reports from memory. Everything is organized and ready when you need it.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-6">
                <i className="ri-team-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-medium text-gray-800 mb-4" style={{fontFamily: '"Playfair Display", serif'}}>Better Team Communication</h3>
              <p className="text-gray-600 font-light">
                Everyone stays on the same page with real-time updates. No more phone tag or missed information.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-full mx-auto mb-6">
                <i className="ri-star-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-2xl font-medium text-gray-800 mb-4" style={{fontFamily: '"Playfair Display", serif'}}>Professional Documentation</h3>
              <p className="text-gray-600 font-light">
                Impress Regional Center coordinators with clear, consistent reports that show real progress and dedication.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-purple-500 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-light mb-6" style={{fontFamily: '"Playfair Display", serif'}}>
            Save hours of stress each month.
          </h2>
          <p className="text-xl mb-8 opacity-90 leading-relaxed font-light">
            Join hundreds of families who've already switched from paper chaos to organized care coordination.
          </p>

          <div className="bg-white/10 backdrop-blur rounded-2xl p-8 mb-8">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-light mb-2" style={{fontFamily: '"Playfair Display", serif'}}>2 min</div>
                <div className="text-sm opacity-80 font-light">Average time per session note</div>
              </div>
              <div>
                <div className="text-3xl font-light mb-2" style={{fontFamily: '"Playfair Display", serif'}}>30 sec</div>
                <div className="text-sm opacity-80 font-light">To generate a professional report</div>
              </div>
              <div>
                <div className="text-3xl font-light mb-2" style={{fontFamily: '"Playfair Display", serif'}}>14 days</div>
                <div className="text-sm opacity-80 font-light">Completely free to try everything</div>
              </div>
            </div>
          </div>

          <form
            id="how-it-works-trial"
            action="https://formspree.io/f/mkgnqokv"
            method="POST"
            data-readdy-form
            className="max-w-md mx-auto mb-6"
          >
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                required
                className="flex-1 px-4 py-3 rounded-full text-gray-800 text-lg font-light"
              />
              <button
                type="submit"
                className="bg-white text-purple-700 px-8 py-3 rounded-full text-lg font-medium hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Try It Free for 14 Days
              </button>
            </div>
          </form>
          <p className="text-sm opacity-75 font-light">No credit card required • Cancel anytime • Full access to everything</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <Link to="/" className="flex items-center mb-4 cursor-pointer">
                <img
                  src="https://static.readdy.ai/image/d58764125c7980aecc839df102219a66/870d5e2d6ad7495c633feeec16bc35ed.png"
                  alt="Care Entry"
                  className="h-16 w-auto brightness-0 invert"
                />
              </Link>
              <p className="text-gray-400 font-light">Bringing families and care teams together.</p>
            </div>
            <div>
              <h4 className="font-medium mb-4" style={{fontFamily: '"Playfair Display", serif'}}>Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link to="/" className="hover:text-white cursor-pointer">
                    Features
                  </Link>
                </li>
                <li>
                  <Link to="/how-it-works" className="hover:text-white cursor-pointer">
                    How It Works
                  </Link>
                </li>
                <li>
                  <a href="#pricing" className="hover:text-white cursor-pointer">
                    Pricing
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4" style={{fontFamily: '"Playfair Display", serif'}}>Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a href="#faq" className="hover:text-white cursor-pointer">
                    FAQ
                  </a>
                </li>
                <li>
                  <a href="#contact" className="hover:text-white cursor-pointer">
                    Contact
                  </a>
                </li>
                <li>
                  <a href="#help" className="hover:text-white cursor-pointer">
                    Help Center
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4" style={{fontFamily: '"Playfair Display", serif'}}>Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a href="#privacy" className="hover:text-white cursor-pointer">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="#terms" className="hover:text-white cursor-pointer">
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">
                    Made with Readdy
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-purple-300">
            <p className="font-light">&copy; 2024 Care Entry. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
